import FormTitle from '@core/components/FormTitle/FormTitle';
import { DateInput, TextInput } from '@core/components/Inputs';
import ReadOnlyInput from '@core/components/Inputs/ReadOnlyInput';
import { SelectInput } from '@core/components/Select';
import { alterarParcelasSubsequentes, calculaDiferencaParcelasUtil, gerarParcelas } from '@core/util/parcelaUtil';
import FuseUtils from '@fuse/utils';
import { Grid } from '@material-ui/core';
import { Categorias } from 'app/components/Categorias';
import { useFormikContext } from 'formik';
import moment from 'moment';
import NP from 'number-precision';
import React from 'react';
import CompraAnexo from '../../CompraAnexo';
import CompraDocumentoFiscalInput from '../../CompraDocumentoFiscal';
import CompraFormaPagamento from '../../CompraFormaPagamento';
import CompraProdutoList from '../../CompraProdutoList/CompraProdutoList';
import DadosCompra from '../../DadosCompra';
import { CamposAproveitamentoCredito } from '../CompraAproveitamentoCredito';
import { compraProdutoFormStyles } from './styles';

const getCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const CompraProdutoForm = ({ empresaIbsCbs }) => {
  const formik = useFormikContext();

  const styles = compraProdutoFormStyles();

  let compraPaga = false;

  if (formik.values?.pagamento?.condicaoPagamento === 'A_VISTA') {
    compraPaga = !!formik.values?.pagamento?.avista?.contaBaixada;
  } else if (
    formik.values?.pagamento?.condicaoPagamento === 'PARCELADO' &&
    formik.values?.pagamento?.parcelado?.parcelas?.length
  ) {
    compraPaga = !!formik.values?.pagamento?.parcelado?.parcelas
      ?.map((parcela) => parcela?.dataPagamento)
      ?.some((dataPagamento) => !!dataPagamento);
  }

  const resetParcelas = () => {
    if (formik.values?.pagamento?.parcelado?.parcelas?.length) {
      formik.setFieldValue(`pagamento.parcelado.parcelas`, []);
      formik.setFieldValue('pagamento.parcelado.diferencaParcelas', 0);
    }
  };

  const handleAddProduto = () => {
    const { produtos } = formik.values;

    produtos.push({ key: FuseUtils.generateGUID() });

    formik.setFieldValue('produtos', produtos);

    resetParcelas();
  };

  const handleRemoveProduto = () => {
    resetParcelas();
    changeValorTotal();
  };

  // const valorTotal = 0;

  /*
   * RNE-213 - Alteração das informações referentes às parcelas - compra de produto
   *
   * Se um dos campos "Valor", "Quantidade de parcelas" for alterado no lançamento
   * de compra parcelada, o sistema deve apagar as parcelas que foram geradas e, somente
   * quando o usuário acionar para "Gerar", o sistema deve distribuir o valor igualmente
   * nas parcelas conforme novas informações alteradas.
   *
   * Se os valores forem alterados diretamente nas parcelas, o sistema não deve alterar
   * os outros valores das parcelas automaticamente.
   */
  const changeValorTotal = (e) => {
    let valorTotal = 0;

    if (!e?.target?.id) {
      return;
    }

    const index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));

    for (let i = 0; i < formik.values.produtos?.length || 0; ++i) {
      const produto = formik.values.produtos[i];

      const valor = (index === i && e.target.id.includes('valor') ? e.target.value : produto?.valor) || 0;
      const quantidade =
        (index === i && e.target.id.includes('quantidade') ? e.target.value : produto?.quantidade) || 0;

      valorTotal = NP.plus(valorTotal, NP.times(quantidade, valor));
    }

    formik.setFieldValue('valorTotal', valorTotal);
    calcularDiferencaCategorias(undefined, valorTotal);
  };

  const valorTotalText = getCurrency(formik.values?.valorTotal);

  /*
   * RNE-228 - Exibição da informação de "Diferença" : Public <<Functional>> Feature 
   * O sistema deve manter a exibição da informação "Diferença" quando o somatório dos valores das parcelas for diferente do valor líquido.

   * Diferença = Valor líquido - Somatório dos valores informados nas parcelas.
   */
  const calculaDiferencaParcelas = (e) => {
    let diferencaParcelas = 0;
    let index = -1;

    if (e?.target?.id?.includes('valorParcela')) {
      index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));
    }

    if (formik.values.pagamento?.parcelado?.parcelas?.length) {
      let parcelas = formik.values.pagamento?.parcelado?.parcelas;
      const { valorTotal } = formik.values;

      parcelas = parcelas.map((parcela, i) => {
        if (index === i) {
          return { ...parcela, valorParcela: e.target.value };
        }
        return parcela;
      });

      diferencaParcelas = calculaDiferencaParcelasUtil(parcelas, valorTotal);

      formik.setFieldValue('pagamento.parcelado.diferencaParcelas', diferencaParcelas);
    }
  };

  const calcularDiferencaCategorias = (event, valorTotal, index = -1) => {
    const {
      values: { categorias },
      setFieldValue,
    } = formik;

    let totalCategorias = 0;

    categorias.forEach((categoria, i) => {
      if (index !== i) {
        totalCategorias += categoria.valorBruto ? categoria.valorBruto : 0;
      } else {
        totalCategorias += event.target.value ? event.target.value : 0;
      }
    });

    valorTotal = valorTotal?.toFixed(2);
    setFieldValue('diferencaCategorias', valorTotal - totalCategorias);
  };

  const handlePreencherDataVencimento = (dataCompra) => {
    const dataCompraClonado = dataCompra.clone();
    if (dataCompra) {
      formik.setFieldValue(`pagamento.avista.dataVencimento`, dataCompraClonado);
    }
  };

  /*
   * RNE-213 - Alteração das informações referentes às parcelas - compra de produto : Public <<Functional>> Feature
   * Se a data da compra ou a data da primeira parcela for alterada, o sistema deve alterar as datas das outras parcelas também, considerando sempre cada parcela de 30 em 30 dias.
   */
  const handleChangeDataPrimeiraParcela = (dataVencimento) => {
    const parcelas = formik.values?.pagamento?.parcelado?.parcelas;
    if (dataVencimento && parcelas?.length) {
      const primeiraDataVencimento = dataVencimento.clone();

      const novasParcelas = alterarParcelasSubsequentes(parcelas, primeiraDataVencimento);

      formik.setFieldValue('pagamento.parcelado.parcelas', novasParcelas);
    }
  };

  const handleGerarParcelas = () => {
    const quantidade = formik.values?.pagamento?.parcelado?.quantidade || 0;
    const dataPrimeiraParcela = formik?.values?.dataCompra.clone().add(30, 'day') || moment();

    const { valorTotal } = formik.values;

    const parcelas = gerarParcelas(quantidade, dataPrimeiraParcela, valorTotal);

    formik.setFieldValue(`pagamento.parcelado.parcelas`, parcelas);
  };

  const handleChangeConta = (id, value) => {
    formik.setFieldValue(id, value);
  };

  const handleChangeCondicaoPagamento = (e) => {
    formik.setFieldValue('pagamento.avista.condicaoPagamento', e.target.value);
    formik.setFieldValue('pagamento.parcelado.condicaoPagamento', e.target.value);

    formik.handleChange(e);
  };

  const handleChangePagamento = (e) => {
    formik.handleChange(e);

    if (e?.target?.id === 'pagamento.avista.conta') {
      formik.setFieldValue('pagamento.avista.formaPagamento', null);
    }

    if (e?.target?.id === 'pagamento.avista.compraPaga') {
      // o switch e o formik fazem com que o true vire "false"
      if (e.target.value === 'false') {
        formik.setFieldValue('pagamento.avista.dataPagamento', formik.values.dataCompra);
        formik.setFieldValue('pagamento.avista.dataLiquidacao', formik.values.dataCompra);
      }
    }

    if (e?.target?.id === 'pagamento.condicaoPagamento') {
      if (e?.target?.value === 'A_VISTA') {
        formik.setFieldValue('pagamento.avista.condicaoPagamento', e.target.value);
      }
    }

    if (e?.target?.id?.includes('pagamento.parcelado.parcelas') && e?.target?.id?.includes('valorParcela')) {
      calculaDiferencaParcelas(e);
    }
    if (e?.target?.id?.includes('quantidade')) {
      resetParcelas();
    }
  };

  const handleChangeProdutos = (e) => {
    formik.handleChange(e);
    if (e?.target?.id?.includes('valor') || e?.target?.id?.includes('quantidade')) {
      changeValorTotal(e);
      resetParcelas();
    }
  };

  const handleAddAnexo = (anexo) => {
    const { anexos = [] } = formik.values;

    anexos.push(anexo);

    // formik.setFieldValue(`anexos[${anexos.length - 1}]`, anexo);
    formik.handleChange({ target: { id: `anexos[${anexos.length - 1}]`, value: anexo } });

    // o Formik não seta o formulário como dirty na linha de cima
    formik.handleChange({ target: { id: 'random', value: Math.random() } });
  };

  const handleRemoveAnexo = (anexo) => {
    const { anexos = [] } = formik.values;

    const novosAnexos = anexos.filter((a) => a?.id !== anexo?.id);

    formik.handleChange({ target: { id: 'anexos', value: novosAnexos } });
  };

  const _errors = {
    categorias: formik.values?.categorias?.map((_categoria, index) => {
      return {
        classe:
          (formik.submitCount > 0 || (formik.touched?.categorias && formik.touched?.categorias[index]?.classe)) &&
          formik.errors?.categorias &&
          formik.errors?.categorias[index]?.classe,
        valorBruto:
          (formik.submitCount > 0 || (formik.touched?.categorias && formik.touched?.categorias[index]?.valorBruto)) &&
          formik.errors?.categorias &&
          formik.errors?.categorias[index]?.valorBruto,
      };
    }),
  };

  const onChangeTipoAproveitamentoCredito = (event) => {
    formik.handleChange(event);
    formik.setFieldValue('aproveitamentoCredito.aliquotaIbs', '');
    formik.setFieldValue('aproveitamentoCredito.aliquotaCbs', '');
    formik.setFieldValue('aproveitamentoCredito.observacaoJustificativa', '');
    if (event.target.value === 'CREDITO_INTEGRAL') {
      formik.setFieldValue('aproveitamentoCredito.percentualCredito', 100);
    } else {
      formik.setFieldValue('aproveitamentoCredito.percentualCredito', '');
    }
  };

  const valorTotalCategorias = formik.values?.categorias.reduce((acc, categoria) => {
    const valorItem = Number(categoria?.valorBruto) || 0;
    return NP.plus(acc, valorItem);
  }, 0);

  return (
    <>
      <DadosCompra
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        onChangePreencherDataVencimento={handlePreencherDataVencimento}
        onChangeDataPrimeiraParcela={handleChangeDataPrimeiraParcela}
        values={formik.values}
        touched={formik.touched}
        errors={formik.errors}
        submitCount={formik.submitCount}
        compraPaga={compraPaga}
      />

      <Grid container spacing={3} className="mt-16">
        <Categorias
          categorias={formik.values?.categorias}
          handleChange={formik.handleChange}
          handleChangeValorBruto={(event, index) => {
            formik.handleChange(event);
            calcularDiferencaCategorias(event, formik.values.valorTotal, index);
          }}
          handleBlur={formik.handleBlur}
          disabledClasse={compraPaga}
          disabledValor={compraPaga}
          errors={_errors}
          natureza="DESPESA"
          showDiferenca
          diferencaCategorias={formik.values?.diferencaCategorias}
        />
      </Grid>

      {/* <FormTitle title="Produtos" /> */}

      <CompraProdutoList
        id="produtos"
        name="produtos"
        errors={formik.errors.produtos}
        touched={formik.touched.produtos}
        value={formik.values.produtos}
        onAddProduto={handleAddProduto}
        onChange={handleChangeProdutos}
        onBlur={formik.handleBlur}
        onRemoveProduto={handleRemoveProduto}
        submitCount={formik.submitCount}
        compraPaga={compraPaga}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ReadOnlyInput
            className={styles.valorTotal}
            id="valorTotal"
            label="Valor total da compra (R$)"
            value={valorTotalText}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="mb-16">
          <TextInput
            id="observacao"
            name="observacao"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.observacao}
            maxLength={500}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Observação"
            disabled={compraPaga}
          />
        </Grid>
      </Grid>

      <FormTitle title="Informações da Nota Fiscal" />
      <Grid container spacing={3} style={{ marginTop: 4, marginBottom: 8 }}>
        <Grid item xs={4}>
          <CompraDocumentoFiscalInput
            label="Documento fiscal"
            id="documentoFiscal"
            format="###############"
            value={formik.values.documentoFiscal}
            fornecedor={formik.values?.fornecedor}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={compraPaga}
            compraId={formik.values?.id}
          />
        </Grid>
        <Grid item xs={4}>
          <DateInput
            id="dataEmissao"
            name="dataEmissao"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.dataEmissao}
            fullWidth
            variant="outlined"
            label="Data da emissão"
            disabled={compraPaga}
          />
        </Grid>
      </Grid>

      {empresaIbsCbs && (
        <>
          <FormTitle title="Aproveitamento de crédito IBS/CBS" />
          <Grid container spacing={3} style={{ marginTop: 4, marginBottom: 8 }}>
            <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
              <SelectInput
                id="aproveitamentoCredito.tipoAproveitamentoCredito"
                label="Aproveitamento de crédito?"
                value={formik.values?.aproveitamentoCredito?.tipoAproveitamentoCredito}
                onChange={(event) => {
                  onChangeTipoAproveitamentoCredito(event);
                }}
                onBlur={formik.handleBlur}
                options={[
                  { id: 'NAO_APROVEITA_CREDITO', title: 'Não aproveita crédito' },
                  { id: 'CREDITO_INTEGRAL', title: 'Crédito integral' },
                  { id: 'CREDITO_PARCIAL', title: 'Crédito parcial' },
                ]}
                touched={!!formik.errors.aproveitamentoCredito?.tipoAproveitamentoCredito}
                error={formik.errors.aproveitamentoCredito?.tipoAproveitamentoCredito}
                required
                disabled={compraPaga}
              />
            </Grid>
            {formik?.values?.aproveitamentoCredito?.tipoAproveitamentoCredito &&
              formik?.values?.aproveitamentoCredito?.tipoAproveitamentoCredito !== 'NAO_APROVEITA_CREDITO' && (
                <CamposAproveitamentoCredito
                  formik={formik}
                  valorTotalCategorias={valorTotalCategorias}
                  disabled={compraPaga}
                />
              )}
          </Grid>
        </>
      )}

      <CompraFormaPagamento
        id="pagamento"
        name="pagamento"
        disabled={!!formik.values.id}
        onBlur={formik.handleBlur}
        onChange={handleChangePagamento}
        values={formik.values.pagamento}
        touched={formik.touched.pagamento}
        errors={formik.errors.pagamento}
        onGerarParcelas={handleGerarParcelas}
        onChangeConta={handleChangeConta}
        onChangeCondicaoPagamento={handleChangeCondicaoPagamento}
        onChangeDataPrimeiraParcela={handleChangeDataPrimeiraParcela}
        submitCount={formik.submitCount}
        compraPaga={compraPaga}
        valorTotal={formik.values?.valorTotal}
        compraId={formik.values?.id}
      />

      <CompraAnexo id="anexos" values={formik?.values?.anexos} onAdd={handleAddAnexo} onRemove={handleRemoveAnexo} />
    </>
  );
};

export default CompraProdutoForm;
